/* @flow */
/* eslint-env es6,browser */
/**
 * @description module for utility functions
 * @module utils
 * @requires module:utils/BrowserUtil
 */

import { BrowserUtil } from './utils/browserutil';
import { version as _apiVersion, name as _apiName } from '../../package.json';

/**
 * returns JSAPI library name
 * @export apiName
 * @returns {string} - JSAPI Name
 */
export function apiName(): string {
  return _apiName;
}

/**
 *
 * JSAPI library version
 * @export apiVersion
 * @returns {string} - JSAPI Versions string
 */
export function apiVersion(): string {
  return _apiVersion;
}

/* eslint-disable no-empty-function*/
/**
 * noop function
 * Utility function can be used for empty callback etc.,
 * @returns {undefined}
 */
export function noop() {}
/* eslint-enable no-empty-function*/

let objProto = Object.prototype,
  toStr = objProto.toString;

/**
 * Null check
 * @param {*} obj  - Object to check
 * @returns {boolean} - Is given object is Null?
 */
export function isNull<T>(obj: T): boolean {
  return toStr.call(obj) === '[object Null]';
}

/**
 * Undefined check
 * @param {*} obj  - Object to check
 * @returns {boolean} - Is given object is undefined?
 */
export function isUndefined<T>(obj: T): boolean {
  return toStr.call(obj) === '[object Undefined]';
}
/**
 * Valid object check
 * @param {T} obj - Object to check
 * @returns {boolean} - Is it valid (Not null & not undefined)?
 */
export function isValid<T>(obj: T): boolean {
  return !isNull(obj) && !isUndefined(obj);
}

/**
 * Checks if obj is function or not
 * @param {*} obj - Object to check
 * @return {boolean} - Is it a Function?
 */
export function isFunction<T>(obj: T): boolean {
  return isValid(obj) && toStr.call(obj) === '[object Function]';
}

/**
 * Checks if obj is object or not
 * @param {*} obj - Object to check
 * @return {boolean} - Is it an Object?
 */
export function isObject<T>(obj: T): boolean {
  return isValid(obj) && toStr.call(obj) === '[object Object]';
}

/**
 * Checks if obj is array or not
 * @param {*} obj - Object to check
 * @return {boolean} - Is it an Array?
 */
export function isArray<T>(obj: T): boolean {
  return isValid(obj) && toStr.call(obj) === '[object Array]';
}

/**
 * Checks if obj is string or not
 * @param {*} obj - Object to check
 * @return {boolean} - Is it a String?
 */
export function isString<T>(obj: T): boolean {
  return isValid(obj) && toStr.call(obj) === '[object String]';
}

/**
 * Checks if obj is number or not
 * @param {*} obj - Object to check
 * @return {boolean} - Is it a number?
 */
export function isNumber<T>(obj: T): boolean {
  return isValid(obj) && toStr.call(obj) === '[object Number]';
}

/**
 * Removes duplicates in given array
 * @param {Array<T>} arr - Input array
 * @return {Array<T>} - array with unique elements
 */
export function unique<T>(arr: Array<T>): Array<T> {
  if (!isArray(arr)) {
    return [];
  }
  var i = 0,
    j = arr.length,
    ret = [];
  for (; i < j; i++) {
    if (ret.indexOf(arr[i]) === -1) {
      ret.push(arr[i]);
    }
  }
  return ret;
}

/**
 * Find intersection between two given arrays
 * @param {Array<T>} arr1 - 1st Array
 * @param {Array<T>} arr2 - 2nd Array
 * @return {Array<T>} - Intersection result
 */
export function intersect<T>(arr1: Array<T>, arr2: Array<T>): Array<T> {
  var temp = [];
  for (var i = 0; i < arr1.length; i++) {
    for (var j = 0; j < arr2.length; j++) {
      if (arr1[i] === arr2[j]) {
        temp.push(arr1[i]);
        break;
      }
    }
  }
  return temp;
}

/**
 * Find difference between two given array
 * @param {Array<T>} arr - 1st Array
 * @param {Array<T>} arr2 - 2nd Array
 * @returns {Array<T>} - Result with difference
 */
export function difference<T>(arr: Array<T>, arr2: Array<T>): Array<T> {
  if (!isArray(arr) || !isArray(arr2)) {
    return [];
  }
  var ret = arr.slice(),
    i = 0,
    j = arr2.length,
    ind;
  for (; i < j; i++) {
    ind = ret.indexOf(arr2[i]);
    if (ind !== -1) {
      ret.splice(ind, 1);
    }
  }
  return ret;
}

/**
 * FisherYates shuffle
 * http://stackoverflow.com/a/6274398
 * https://bost.ocks.org/mike/shuffle/
 * @param {Array<T>} arr - Array to be shuffled
 * @return {Array<T>} - returns copy of shuffled array
 */
export function shuffle<T>(arr: Array<T>): Array<T> {
  let array = arr.slice();
  let counter = array.length;

  // while there are elements in the array
  while (counter > 0) {
    // pick a random index
    let index = Math.floor(Math.random() * counter);

    // decrease counter by 1
    counter--;

    // and swap the last element with it
    let temp = array[counter];
    array[counter] = array[index];
    array[index] = temp;
  }

  return array;
}

const rbracket = /\[]$/;
/**
 * @global
 * @type {function}
 * @callback paramAdd
 * @param {string} key
 * @param {string|function()} valOrFunction
 */
/**
 * Simplified version of PARAM serialize code from jquery
 * @access private
 * @param {string} prefix - Prefix string
 * @param {(Object|Array<*>|string|number)} o - child object to be serialized
 * @param {paramAdd} add - Callback fn
 * @returns {undefined}
 */
function buildParams(
  prefix: string,
  o: Object | Array<any> | string | number,
  add: (string, string | (() => any)) => any
) {
  if (isObject(o)) {
    //iterate and add object values
    //$FlowFixMe
    Object.keys(o).forEach((v: string) => {
      //$FlowFixMe
      buildParams(prefix + '[' + v + ']', o[v], add);
    });
  } else if (isArray(o)) {
    //iterate and add all array items
    //$FlowFixMe
    o.forEach((v: any, i: number) => {
      if (rbracket.test(prefix)) {
        // Treat each array item as a scalar.
        add(prefix, v);
      } else {
        buildParams(
          prefix + '[' + (typeof v === 'object' && v !== null ? i : '') + ']',
          v,
          add
        );
      }
    });
  } else {
    //scalar item
    //$FlowFixMe
    add(prefix, o);
  }
}

/**
 * Utility funciton for building jsonp params
 * @param {Object} a - parameters
 * @return {string} - Serialized param
 */
// prettier-ignore
export function param(a: Object): string {
  let s = [],
    enc = encodeURIComponent,
    add = (key: string, valueOrFunction: string | (() => any)) => {
      // If value is a function, invoke it and use its return value
      // $FlowFixMe
      let value: string = isFunction(valueOrFunction) ? valueOrFunction()
        : valueOrFunction;
      s[s.length] = enc(key) + '=' + enc(value === null ? '' : value);
    };
  Object.keys(a).forEach((v: string) => {
    buildParams(v, a[v], add);
  });
  return s.join('&');
}

/**
 * Convert cents to dollar
 * @param {number} c - Cents to be converted
 * @returns {string} - Dollar value
 */
export function centsToDollar(c: number): string {
  c = c || 0;
  return (c / 100).toFixed(2);
}

/**
 * jQuery extend functionality
 * Supports variable number of arguments
 * @param {boolean|Object} deepArg - Boolear for deep merge OR first object
 * @param {Array<Object>} args - Remainint objects to be merged
 * @return {Object} - Extended result object
 */
export function extend(
  deepArg: boolean | Object,
  ...args: Array<Object>
): Object {
  var options,
    name,
    src,
    copy,
    copyIsArray,
    clone,
    target = deepArg || {},
    i = 0,
    length = args.length,
    deep = false;

  // Handle a deep copy situation
  if (typeof target === 'boolean') {
    deep = target;
    target = args[0] || {};
    // skip the boolean and the target
    i = 1;
  }

  // Handle case when target is a string or something (possible in deep copy)
  if (typeof target !== 'object' && !isFunction(target)) {
    target = {};
  }

  for (; i < length; i++) {
    // Only deal with non-null/undefined values
    if ((options = args[i]) !== null) {
      // Extend the base object
      /* eslint-disable no-continue */
      for (name in options) {
        if (!{}.hasOwnProperty.call(options, name)) {
          continue; //ignore inherited properties
        }
        src = target[name];
        copy = options[name];

        // Prevent never-ending loop
        if (target === copy) {
          continue;
        }
        /* eslint-enable no-continue */
        // Recurse if we're merging plain objects or arrays
        if (deep && copy && (isObject(copy) || (copyIsArray = isArray(copy)))) {
          if (copyIsArray) {
            copyIsArray = false;
            clone = src && isArray(src) ? src : [];
          } else {
            clone = src && isObject(src) ? src : {};
          }

          // Never move original objects, clone them
          //$FlowFixMe
          target[name] = extend(deep, clone, copy);

          // Don't bring in undefined values
        } else if (copy !== undefined) {
          target[name] = copy;
        }
      }
    }
  }
  // Return the modified object
  return target;
}

/* eslint-enable arrow-body-style */
var utilLoggerGif = '';
const debugConsole = location.href.indexOf('__jsapi_debugconsole=1') !== -1;

/**
 * setter for logger gif
 * @param {string} loggerGif - Img URL
 * @returns {undefined}
 */
export function setLoggerGif(loggerGif: string) {
  utilLoggerGif = loggerGif;
}

/**
 * Utility function for sending logger gif request (From global.js in EOW)
 * @param {string} cmd - Log message to send
 * @param {Object} o - usr & count are not allowed/overwritten here
 * @returns {undefined}
 */
export function sendLoggerImg(cmd: string, o: {}) {
  cmd = cmd || '';
  o = o || {};
  if (!cmd.length || !utilLoggerGif.length) {
    return;
  }

  //deep copy & merging args
  extend(true, o, o, {
    command: cmd,
    count: new Date().getTime()
  });

  //convert into query param
  let beaconUrl = `${utilLoggerGif}?${param(o)}`;
  /*if (sendBeacon(beaconUrl)) {
    return;
  }*/

  // sendBeacon is not supported, send image tracking request
  var img = document.createElement('img');
  img.src = beaconUrl;
}

/**
 * Logging utility function
 * @access private
 * @param {string} s  - string to be logged
 * @param {boolean} appLogging - Should log to back-end
 * @returns {undefined}
 */
function _log(s: string, appLogging: boolean) {
  s = s || '';
  /*eslint-disable no-console*/
  if (typeof console !== 'undefined' && debugConsole === true) {
    let c = console;
    c.log(s);
  }
  /*eslint-enable no-console*/
  if (appLogging === true) {
    sendLoggerImg('ADLOG', { msg: s });
  }
}

/**
 * function to log debug log
 * @param {string} s - string to be logged
 * @returns {undefined}
 */
export function logDebug(s: string) {
  s = 'AS:D:' + s;
  _log(s, false);
}

/**
 * function to log error log
 * @param {string} s - string to be logged
 * @returns {undefined}
 */
export function logError(s: string) {
  s = 'AS:E:' + s;
  _log(s, true);
}

/**
 * function to log info log
 * @param {string} s - string to be logged
 * @returns {undefined}
 */
export function logInfo(s: string) {
  s = 'AS:I:' + s;
  _log(s, true);
}

/**
 * parse JSON ad response, take care of removing OAS tags
 * Need to check this during openx migration!!
 * https://github.com/rollup/rollup/wiki/Troubleshooting#avoiding-eval
 * @param {window} w - window object
 * @param {string} data - data to be parsed
 * @return {Object} - Parsed JSON data
 */
export function parseJSON(w: window, data: string): any {
  /* jshint evil: true */
  var b = data.indexOf('{'),
    e = data.lastIndexOf('}'),
    s = '';
  if (b !== -1 && e !== -1 && e > b) {
    s = data.substring(b, e + 1);
  }
  if (s.length) {
    try {
      if (typeof w.JSON === 'object' && isFunction(w.JSON.parse)) {
        return w.JSON.parse(s);
      }
      if (isFunction(w.jQuery) && isFunction(w.jQuery.parseJSON)) {
        return w.jQuery.parseJSON(s);
      }
      var eval2 = w.eval;
      return eval2('(' + s + ')');
    } catch (ex) {
      /*continue regardless of error*/
    }
  }
  return {};
}

/**
 * load a JS file asynchronously.
 * [c]2014 @scottjehl, Filament Group, Inc.
 * (Based on http://goo.gl/REQGQ by Paul Irish).
 * Licensed MIT
 * https://github.com/filamentgroup/loadJS/blob/master/loadJS.js
 * @param {string} src - js file url
 * @param {Document} d - document object
 * @param {boolean} [bAsync=false] - async flag
 * @return {HTMLScriptElement} - Newly created script DOM node
 */
export function loadJS(
  src: string,
  d: Document,
  bAsync?: boolean
): HTMLScriptElement | null {
  'use strict';
  bAsync = bAsync || false;
  src = src || '';
  if (!src.length) {
    return null;
  }
  var script = d.createElement('script');
  script.src = src;
  if (bAsync) {
    script.async = true;
  }
  var ref = d.getElementsByTagName('script')[0];
  if (ref && ref.parentNode) {
    ref.parentNode.insertBefore(script, ref);
  } else {
    try {
      if (d.body) {
        d.body.appendChild(script);
      }
    } catch (e) {
      /**/
    }
  }
  return script;
}

/**
 * Loads js file asynchronously
 * @global
 * @typedef {Object} loadJSWithTimeoutParams
 * @property {Object} [content=window]
 * @property {string} [function]
 * @property {string} [object]
 * @param {string} src - js file url
 * @param {Document} d - document object
 * @param {boolean} bAsync - async flag
 * @param {number} timeout - timeout for loading
 * @param {loadJSWithTimeoutParams} [o] - object to check for context/function
 * @return {Promise<*>} - Promise for loading JS with timeout
 */
export function loadJSWithTimeout(
  src: string,
  d: window.document,
  bAsync: boolean,
  timeout: number,
  o?: { context?: window, func?: string, object?: string }
): Promise<*> {
  var script = loadJS(src, d, bAsync),
    done = false,
    loadTimer: ?TimeoutID = null;
  o = o || {};
  o.context = o.context || window;
  o.func = o.func || '';
  o.object = o.object || '';

  if (!script) {
    return Promise.reject(`Script loading failed ${src}`);
  }
  //returns promise
  //prettier-ignore
  return new Promise((resolve, reject) => {
    function loadHandler() {
      let err = `${src} script loading failed ${loadTimer ? ': timeout' : ''}`;
      // $FlowFixMe
      if (o.func.length || o.object.length) {
        // $FlowFixMe
        if ((!o.func.length || isFunction(o.context[o.func])) &&
          // $FlowFixMe
          (!o.object.length || isObject(o.context[o.object]))
        ) {
          return resolve();
        }
        logError(`${err} :context check failed`);
        return reject(err);
      }
      if (loadTimer) {
        logError(err);
        return reject(err);
      }
      return resolve();
    }

    if (!script) {
      reject(`Script loading failed ${src}`);
      return;
    }
    //load AOL script here
    loadTimer = setTimeout(loadHandler, timeout);
    // Attach handlers for all browsers
    /* eslint-disable unicorn/prefer-add-event-listener */
    script.onload = script.onreadystatechange = function() {
      if (
        !done &&
        (!this.readyState ||
          this.readyState === 'loaded' ||
          this.readyState === 'complete')
      ) {
        done = true;
        if(loadTimer) {
          clearTimeout(loadTimer);
          loadTimer = null;
        }
        loadHandler();
        if (!script) {
          return;
        }
        script.onload = script.onreadystatechange = null;
      }
    };
    /* eslint-enable unicorn/prefer-add-event-listener */
  });
}

/**
 * load a CSS file asynchronously.
 * [c]2014 @scottjehl, Filament Group, Inc.
 * Licensed MIT
 * https://github.com/filamentgroup/loadCSS/blob/master/loadCSS.js
 * @param {string} href - link src url
 * @param {Document} d - document object
 * @param {Element} [before] - element before which link tag needs to be inserted
 * @param {string} [media=all] - media type
 * @return {HTMLLinkElement} - Newly created link tag for loading CSS
 */
export function loadCSS(
  href: string,
  d: Document,
  before?: Element,
  media?: string
): HTMLLinkElement {
  'use strict';
  // Arguments explained:
  // `href` is the URL for your CSS file.
  // `before` optionally defines the element we'll use as a reference for injecting our <link>
  // By default, `before` uses the first <script> element in the page.
  // However, since the order in which stylesheets are referenced matters, you might need a more specific location in your document.
  // If so, pass a different reference element to the `before` argument and it'll insert before that instead
  // note: `insertBefore` is used instead of `appendChild`, for safety re: http://www.paulirish.com/2011/surefire-dom-element-insertion/
  var ss: HTMLLinkElement = d.createElement('link');
  var ref: Element = before || d.getElementsByTagName('script')[0];
  ss.rel = 'stylesheet';
  ss.href = href;
  // temporarily, set media to something non-matching to ensure it'll fetch without blocking render
  ss.media = 'only x';
  // inject link
  if (ref && ref.parentNode) {
    ref.parentNode.insertBefore(ss, ref);
  }
  // set media back to `all` so that the styleshet applies once it loads
  setTimeout(function() {
    ss.media = media || 'all';
  });
  return ss;
}

/**
 * Preload given resource
 * @param {string} href - link src url
 * @param {Document} d - document object
 * @param {string} [as] -  resource type (script/image/style/document)
 * @param {string} [type] - media type
 * @param {boolean} [crossorigin=false] - how to handle cross-origin request
 * @param {boolean} [before] - element before which link tag needs to be inserted
 * @return {HTMLLinkElement} - Newly created link tag for preload
 */
export function preload(
  href: string,
  d: Document,
  as?: string = '',
  type?: string = '',
  crossorigin?: boolean = false,
  before?: Element
): HTMLLinkElement {
  let ref = before || d.getElementsByTagName('script')[0],
    link = d.createElement('link');
  link.href = href;
  link.rel = 'preload';
  if (as.length) {
    //$FlowFixMe
    link.as = as;
  }
  if (type.length) {
    link.type = type;
  }
  if (crossorigin) {
    //$FlowFixMe
    link.crossorigin = true;
  }
  if (ref && ref.parentNode) {
    ref.parentNode.insertBefore(link, ref);
  }
  return link;
}

/**
 * Browser exclusion list support
 * We can exclude based on browser name, version, page name
 * Format: '<name>|<version>|<pagename>,...'
 * '*' to exlude all
 * @example '*|*|write' - Will exclude compose page in all browser
 * @param {string} s - Exclusion list as string
 * @return {Array<string>} - Get browser exclusion list as Array
 */
export function getBrowserExcludeList(s: string): Array<string> {
  var bName = BrowserUtil.getShortName(),
    bVer = BrowserUtil.getMajorVersion(),
    i = 0,
    excludeList = (s || '').toLowerCase().split(','),
    ret = [],
    tmp = '',
    arr = [];
  for (; i < excludeList.length; i++) {
    tmp = excludeList[i];
    arr = tmp.split('|');
    if (tmp.indexOf('*') !== -1 && arr.length === 3) {
      arr[0] = arr[0] === '*' ? bName : arr[0];
      arr[1] = arr[1] === '*' ? String(bVer) : arr[1];
      tmp = arr.join('|');
    }
    ret.push(tmp.toLowerCase());
  }
  return ret;
}

/**
 * Check if current page/browser combo is in exclude list
 * @param {string} pname - pagename
 * @param {Array<string>} exclList - exclude list formulated using getBrowserExcludeList
 * @returns {boolean} - Browser matches with exclusion list (name/version)
 */
export function isBrowserExcluded(
  pname: string,
  exclList: Array<string>
): boolean {
  pname = pname || '';
  exclList = exclList || [];
  if (!pname.length || !exclList.length) {
    return false;
  }
  var prefix =
      BrowserUtil.getShortName() + '|' + BrowserUtil.getMajorVersion() + '|',
    p1 = (prefix + pname).toLowerCase(),
    p2 = (prefix + '*').toLowerCase(),
    bRet = false;
  bRet = exclList.indexOf(p1) !== -1 || exclList.indexOf(p2) !== -1;
  return bRet;
}

/**
 * Generates promise which rejects after timeout
 * @param {number} ms - timeout in millisecs
 * @param {string} cxt - context to be passed in reject message
 * @returns {Promise} - Promise rejects with timeout
 */
export function getTimeoutPromise(ms: number, cxt: string = ''): Promise<*> {
  if (!isNumber(ms) || ms <= 0) {
    return Promise.reject();
  }

  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (cxt.length) {
        cxt += ':';
      }
      reject(cxt + 'timeout');
    }, ms);
  });
}
/* eslint-disable arrow-body-style */

/**
 * RSVP equivalent of allSettled
 * @global
 * @typedef {Object} allSettledPerPromiseResp
 * @property {string} state=(fullfilled|rejected)
 * @property {*} value - data returned in promise resolve
 * @property {*} reason - error reason (data passed to promise reject)
 * @param {Array<Promise<*>>} arr - Array of promise
 * @return {Promise<Array<allSettledPerPromiseResp>>} - Promise returns with fulfilled/rejectd status
 */
//prettier-ignore
export function allSettledPromise(
  arr: Array<Promise<*>>
): Promise<Array<{ state: 'fulfilled' | 'rejected', value: any, reason: any }>> {
  arr = arr || [];
  return Promise.all(
    arr.map(p => {
      return p.then(
        v => {
          return { state: 'fulfilled', reason: '', value: v };
        },
        e => {
          return { state: 'rejected', reason: e, value: '' };
        }
      );
    })
  );
}

/**
 * RSVP equivalent of allSettled with timeout
 * @global
 * @typedef {Object} allSettledTimedPerPromiseResp
 * @property {string} state=(fullfilled|rejected)
 * @property {*} value - data returned in promise resolve
 * @property {*} reason - error reason (data passed to promise reject)
 * @property {string} name - name passed as input for each promise
 * @property {number} tt - time taken
 * @param {Array<Promise<*>>} arr - Array of promise
 * @return {Promise<Array<allSettledTimedPerPromiseResp>>} - Promise returns with fulfilled/rejectd status
 */
//prettier-ignore
export function allSettledTimedPromise(
  arr: Array<{ name: string, promise: Promise<*> }>
): Promise<Array<{
    state: 'fulfilled' | 'rejected',
    value: any,
    reason: any,
    tt: number,
    name: string
  }>> {
  arr = arr || [];
  let startTime = Date.now();
  return Promise.all(
    arr.map(o => {
      return o.promise.then(
        v => {
          return {
            name: o.name,
            state: 'fulfilled',
            reason: '',
            value: v,
            tt: Date.now() - startTime
          };
        },
        e => {
          return {
            name: o.name,
            state: 'rejected',
            reason: e,
            value: '',
            tt: Date.now() - startTime
          };
        }
      );
    })
  );
}

export const F_NOT_INITED = 0;
export const F_INITED = 1;
export const F_FAILED = -1;
export const F_TIMEOUT = -2;

/**
 * Get the iframe/window document
 * @param {HTMLIFrameElement} tempIFrameObj - IFrame element
 * @return {Document} - IFrame document
 */
export function getIfrDoc(tempIFrameObj: HTMLIFrameElement): ?Document {
  var tempIFrameDoc = null;

  if (!tempIFrameObj) {
    return tempIFrameDoc;
  }
  //get the document
  if (tempIFrameObj.contentDocument) {
    tempIFrameDoc = tempIFrameObj.contentDocument;
  } else if (tempIFrameObj.contentWindow) {
    let w: window = tempIFrameObj.contentWindow;
    if (w.document) {
      tempIFrameDoc = w.document;
    }
    //$FlowFixMe
  } else if (tempIFrameObj.document) {
    tempIFrameDoc = tempIFrameObj.document;
  }
  //$FlowFixMe
  return tempIFrameDoc;
}

/**
 * Create iframe element
 * @param {Document} doc -Document DOM object
 * @param {string} id - new IFrame ID
 * @param {number} width - width
 * @param {number} height - height
 * @param {string} [url] - URL to load
 * @param {string} [sandbox] - sandbox attribute
 * @return {HTMLIFrameElement} - new IFrame element
 */
export function createIframe(
  doc: Document,
  id: string,
  width: string | number,
  height: string | number,
  url: ?string,
  sandbox: ?string
): HTMLIFrameElement {
  var newIF = doc.createElement('iframe');

  //set property for new iframe
  newIF.setAttribute('id', id);
  newIF.setAttribute('name', id);
  newIF.setAttribute('topmargin', '0');
  newIF.setAttribute('leftmargin', '0');
  newIF.setAttribute('marginheight', '0');
  newIF.setAttribute('marginwidth', '0');
  newIF.width = String(width);
  newIF.height = String(height);
  newIF.frameBorder = '0';
  newIF.scrolling = 'no';
  //$FlowFixMe
  newIF.allowTransparency = 'true';
  //$FlowFixMe
  newIF.backgroundColor = 'transparant';

  // setting sandbox attribute only if it is defined
  //$FlowFixMe
  if (isString(sandbox) && sandbox.length > 0) {
    //$FlowFixMe
    newIF.setAttribute('sandbox', sandbox);
  }

  if (url && url.length) {
    newIF.src = url;
  }
  return newIF;
}

/**
 * random string generator
 * @return {string} - Random number
 */
export function getRandom(): string {
  return Math.random()
    .toString()
    .substring(2, 11);
}

/**
 * Set iframe height by calculating the dynamic height for the iframe.
 * @param {string} id - IFrame DOM ID
 * @returns {undefined}
 */
export function setIfrHeight(id: string) {
  /** Flow doesn't differentiate HTMLIFrameElement from HTMLElement
   * as we are using getElementById here. Hence override it
   * https://stackoverflow.com/questions/40052760/flow-htmlelement-queryselector-returning-an-iframe
   */
  var currrentObj = ((document.getElementById(id): any): HTMLIFrameElement),
    tempIFrameDoc;
  if (!currrentObj) {
    return;
  }
  tempIFrameDoc = getIfrDoc(currrentObj);
  if (tempIFrameDoc && tempIFrameDoc.body) {
    //Put max of scrollHeight & offsetheight to make it work cross-browser
    // $FlowFixMe
    currrentObj.height = Math.max(
      tempIFrameDoc.body.scrollHeight,
      tempIFrameDoc.body.offsetHeight
    );
    if (tempIFrameDoc && tempIFrameDoc.body && tempIFrameDoc.body.style) {
      tempIFrameDoc.body.style.marginBottom = '0px';
      tempIFrameDoc.body.style.marginRight = '0px';
    }
  }
}

/**
 * attach event to given element
 * @param {Element} elem - DOM element
 * @param {string} type - event type
 * @param {function()} cB - event callback function
 * @returns {undefined}
 */
export function addEvent(elem: Element, type: string, cB: () => any) {
  if (elem && elem.addEventListener) {
    elem.addEventListener(type, cB, false);
  } else if (elem && elem.attachEvent) {
    elem.attachEvent('on' + type, cB);
  }
}

/**
 * remove event attached to an element
 * @param {Element} elem - DOM element
 * @param {string} type - event type
 * @param {function()} cB - event callback function
 * @returns {undefined}
 */
export function removeEvent(elem: Element, type: string, cB: () => any) {
  if (elem && elem.removeEventListener) {
    elem.removeEventListener(type, cB, false);
  } else if (elem && elem.detachEvent) {
    elem.detachEvent('on' + type, cB);
  }
}

export function sendBeacon(url: string) {
  try {
    return navigator && navigator.sendBeacon && navigator.sendBeacon(url);
  } catch (e) {
    logError(`sendBeacon failed ${JSON.stringify(e)}`);
  }
  return false;
}

export function DOMTokenListSupports(tokenList: DOMTokenList, token: string) {
  try {
    //$FlowFixMe
    return tokenList && tokenList.supports && tokenList.supports(token);
  } catch (e) {
    /**/
  }
}

/**
 * checks if link preload is supported in the browser
 * @param {Document} d - Document DOM Node
 * @return {boolean} - True/False
 */
export function isLinkPreloadSupported(d: Document) {
  //$FlowFixMe
  return DOMTokenListSupports(d.createElement('link').relList, 'preload');
}

/**
 * function to preload js file
 * handles browsers where link preload is not supported as well
 * @param {Document} d - document object
 * @param {string} src - url of the resource to be preloaded
 * @param {number} timeout - timeout (used only for browsers where link preload is not supported)
 * @returns {Promise} - Promise for preload processing
 */
export function preloadJS(
  d: Document,
  src: string,
  timeout: number
): Promise<*> {
  try {
    if (isLinkPreloadSupported(d)) {
      preload(src, d, 'script');
      return Promise.resolve();
    }
    let newIfr = createIframe(
      d,
      `__preloadjs${getRandom()}`,
      '0',
      '0',
      'javascript:false'
    );
    if (!d.body) {
      return Promise.resolve();
    }
    d.body.appendChild(newIfr);
    let ifrDoc: ?Document = getIfrDoc(newIfr);
    if (ifrDoc) {
      ifrDoc.open();
      ifrDoc.write('<body>Foo</body>');
      ifrDoc.close();
      return loadJSWithTimeout(src, ifrDoc, true, timeout).then(
        () => d.body && d.body.removeChild(newIfr),
        () => d.body && d.body.removeChild(newIfr)
      );
    }
  } catch (e) {
    /**/
  }
  return Promise.resolve();
}

/**
 * Generic function to preload any resources on supported browser
 * @param {Document} d - document object
 * @param {string} src - url of the resource to be preloaded
 * @param {string} as - type
 * @returns {undefined}
 */
export function preloadResource(
  d: Document,
  src: string = '',
  as?: string = ''
) {
  if (!d || !src || !src.length || !isLinkPreloadSupported(d)) {
    return;
  }

  //link preload supported
  preload(src, d, as);
}

/**
 * Check browser suppport for native JSON processing
 * @returns {boolean} - True/False
 */
export function isJSONSupported() {
  return typeof JSON === 'object' && isFunction(JSON.stringify);
}

/**
 * Check browser suppport for finding effective type of connection
 * and returns the type or returns empty string
 * @returns {string} - {'slow-2g', '2g', '3g', or '4g'}/''
 */
export function getEffectiveType() {
  //$FlowFixMe
  return navigator.connection && navigator.connection.effectiveType
    ? navigator.connection.effectiveType || ''
    : '';
}

/**
 * Get effective type and decide if it is dialup or not,
 * if the effective type is not available return the value
 * passed in parameters
 * @param {boolean} d - dialup value from EOW
 * @returns {boolean} - True/False
 */
export function getEvaluatedDialup(d: boolean) {
  let eType = getEffectiveType();
  if (eType !== '') {
    return eType === 'slow-2g' || eType === '2g';
  }
  return d;
}

/**
 * Returns true if the page is https or
 * Returns false if the page is http
 * @returns {boolean} - true/false
 */
export function isSecure() {
  return location.protocol === 'https';
}

/**
 * Returns true if mobile is supported or
 * Returns false if mobile is not supported
 * @returns {boolean} - true/false
 */
export function isMobile() {
  return (
    /* eslint-disable compat/compat */
    isFunction(document.querySelector) &&
    document.querySelector(
      'meta[name="viewport"][content*="width=device-width"]'
    ) !== null
  );
  /* eslint-enable compat/compat */
}

/**
 * Returns true for no tracking or
 * Returns false for unrestricted tracking
 * @returns {boolean} - true/false
 */
export function isDNT() {
  let dnt =
    //$FlowFixMe
    navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack || '';
  return dnt === 1 || dnt === '1' || dnt === 'yes';
}
